import Tooltip from 'rc-tooltip';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageComponent } from '@/components/Image';
import { useDevice } from '@/hooks/device';
import { getPercentBreakpoins, VipGroupName } from '@/modules/vip/helpers';
import { useCommonStore } from '@/store';

import {
  overlayInnerStyle,
  overlayStyle,
  ScCoin,
  ScProgressBar,
} from './styled';

const ZERO_PROGRESS = '52px';
const ZERO_PROGRESS_MOBILE = '42px';
const SMALL_MODE_ZERO_PROGRESS = '34px';

export type ProgressBarMode = 'passed' | 'passedSuper' | 'future';
export interface ProgressBarProps {
  userVipLevel?: VipGroupName;
  small?: boolean;
}

export const ProgressBar: FC<ProgressBarProps> = ({ small, userVipLevel }) => {
  const userTSGProgressPercentByGroup =
    useCommonStore.use.userTSGProgressPercentByGroup();
  const isPassedSuperSpotBreakpoint =
    useCommonStore.use.isPassedSuperSpotBreakpoint();
  const { t } = useTranslation('vip');

  const { isMobileDevice } = useDevice();

  const percentBreakpoints = getPercentBreakpoins(t, userVipLevel);

  const indexOfCurrentUserLevel = useMemo(() => {
    const validBreakpoints = percentBreakpoints.filter(
      ({ percentage }) => percentage <= userTSGProgressPercentByGroup
    );

    return validBreakpoints.length - 1;
  }, [userTSGProgressPercentByGroup, percentBreakpoints]);

  const progress = useMemo(() => {
    const zeroProgress = isMobileDevice ? ZERO_PROGRESS_MOBILE : ZERO_PROGRESS;

    return userTSGProgressPercentByGroup
      ? `${userTSGProgressPercentByGroup}%`
      : small
        ? SMALL_MODE_ZERO_PROGRESS
        : zeroProgress;
  }, [isMobileDevice, userTSGProgressPercentByGroup, small]);

  // workaround for rc-tootip mobile placement issue
  const getTooltipPlacement = (index: number) => {
    if (!isMobileDevice) return 'top';

    if (index === 0) return 'topLeft';
    if (index === percentBreakpoints.length - 1) return 'topRight';
    return 'top';
  };

  return (
    <ScProgressBar
      small={small}
      progress={progress}
      isPassedSuperSpotBreakpoint={isPassedSuperSpotBreakpoint}
    >
      <div className="progress-line">
        {percentBreakpoints.map((breakpoint, index) => {
          const isPassed = index <= indexOfCurrentUserLevel;

          const mode: ProgressBarMode = isPassed
            ? isPassedSuperSpotBreakpoint
              ? 'passedSuper'
              : 'passed'
            : 'future';

          return (
            <div className="breackpoint" key={breakpoint.percentage}>
              <Tooltip
                placement={getTooltipPlacement(index)}
                showArrow={false}
                overlay={breakpoint.tooltip}
                overlayStyle={overlayStyle}
                overlayInnerStyle={overlayInnerStyle}
                trigger="click"
              >
                <ScCoin mode={mode} small={small}>
                  <ImageComponent
                    src={breakpoint.icon}
                    alt="progress icon"
                    className="progressIcon"
                    width={isMobileDevice || small ? 24 : 32}
                    height={isMobileDevice || small ? 24 : 32}
                  />
                  {breakpoint.boosted && (
                    <ImageComponent
                      src={`/vip/vip-page/progress-bar/boosted-${mode}.webp`}
                      alt="boosted icon"
                      className="progressBoostedIcon"
                      width={isMobileDevice || small ? 20 : 24}
                      height={isMobileDevice || small ? 20 : 24}
                    />
                  )}
                </ScCoin>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </ScProgressBar>
  );
};
